import { useCallback, useState } from 'react';

import { InboxEntry, UserCommunication } from '@this+that/models';

import { useActiveInboxEntriesSlice } from './useActiveInboxEntriesSlice';
import {
  useDeleteInboxEntry as useDeleteInboxEntryService,
  useGetDetailedInboxEntry,
} from '../../services/inboxEntries.service';
import {
  useCommunicationIntentsStore,
  useInboxEntriesStore,
  useUnreadStore,
  useUserCommunicationsStore,
} from '../../store';
import { indexedDBAdapter } from '../../store/utils/indexedDBAdapter';

interface UseDeleteInboxEntryReturn {
  deleteInboxEntry: (inboxEntryId: string) => Promise<boolean>;
  isDeleting: boolean;
  deleteError: Error | null;
}

function resolveTaskGroupId(
  inboxEntryId: string,
  detailedEntryTaskGroupId: string | null | undefined,
  activeTaskGroupId: string | null,
): string | null {
  if (detailedEntryTaskGroupId) {
    return detailedEntryTaskGroupId;
  }

  const storeEntry = useInboxEntriesStore
    .getState()
    .taskManager.activeTaskGroup.inboxEntries.find(
      (e) => e.id === inboxEntryId,
    );

  return storeEntry?.taskGroupId ?? activeTaskGroupId;
}

function cleanupCommunications(
  inboxEntry: InboxEntry,
  clearIntents: (messageIds: string[]) => void,
  removeAndInsertUserCommunication: (communication: UserCommunication) => void,
): void {
  clearIntents(inboxEntry.communicationIds || []);

  if (inboxEntry.communications?.length) {
    inboxEntry.communications.forEach((communication) => {
      // An absent array means "not selected", not "no links" — rewriting it
      // would drop every other DoBox link the message still has.
      if (!communication?.inboxEntryIds) {
        return;
      }

      const remainingInboxEntryIds = communication.inboxEntryIds.filter(
        (id) => id !== inboxEntry.id,
      );

      removeAndInsertUserCommunication({
        ...communication,
        inboxEntryIds:
          remainingInboxEntryIds.length > 0
            ? remainingInboxEntryIds
            : undefined,
      } as UserCommunication);
    });
  }
}

export function useDeleteInboxEntry(): UseDeleteInboxEntryReturn {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<Error | null>(null);

  const { deleteInboxEntry: deleteInboxEntryService } =
    useDeleteInboxEntryService();
  const { getDetailedInboxEntry } = useGetDetailedInboxEntry();
  const { removeInboxEntry } = useActiveInboxEntriesSlice();
  const { markAsRead } = useUnreadStore();
  const { removeAndInsertUserCommunication } = useUserCommunicationsStore();
  const { clearIntents } = useCommunicationIntentsStore();
  const activeTaskGroupId = useInboxEntriesStore(
    (s) => s.taskManager.activeTaskGroup.taskGroupId,
  );

  const deleteInboxEntry = useCallback(
    async (inboxEntryId: string): Promise<boolean> => {
      setIsDeleting(true);
      setDeleteError(null);

      try {
        const inboxEntry = await getDetailedInboxEntry(inboxEntryId);

        if (inboxEntry) {
          cleanupCommunications(
            inboxEntry,
            clearIntents,
            removeAndInsertUserCommunication,
          );
        }

        const success = await deleteInboxEntryService(inboxEntryId);

        if (success) {
          const currentVersion = useInboxEntriesStore
            .getState()
            .taskManager.activeTaskGroup.inboxEntries.find(
              (e) => e.id === inboxEntryId,
            )?.version;

          // The backend soft delete bumps version by 1; tombstoning at that
          // version suppresses the delete's own MODIFY echo too.
          await indexedDBAdapter.markAsDeleted(
            inboxEntryId,
            'inbox-entry',
            (currentVersion ?? 0) + 1,
          );

          const taskGroupId = resolveTaskGroupId(
            inboxEntryId,
            inboxEntry?.taskGroupId,
            activeTaskGroupId,
          );
          if (taskGroupId) {
            markAsRead(inboxEntryId, taskGroupId);
          }
          await removeInboxEntry(inboxEntryId);
        }

        return success;
      } catch (error) {
        const errorInstance =
          error instanceof Error
            ? error
            : new Error('Failed to delete inbox entry');
        setDeleteError(errorInstance);
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [
      activeTaskGroupId,
      deleteInboxEntryService,
      getDetailedInboxEntry,
      removeInboxEntry,
      removeAndInsertUserCommunication,
      clearIntents,
      markAsRead,
    ],
  );

  return {
    deleteInboxEntry,
    isDeleting,
    deleteError,
  };
}
